import orderBy from 'lodash/orderBy';

/**
 * Parses a start-list `columns` config array into show/hide sets.
 *
 * Each entry names a column `key`, optionally signed: `-key` hides it, `+key`
 * or a bare `key` shows it. Since all columns are visible by default, the
 * `shown` set is informational — only `hidden` suppresses anything. The last
 * mention of a key wins.
 *
 * `options` is unvalidated JSONB, so anything that isn't an array of strings
 * degrades to an empty filter rather than throwing.
 */
const parseColumnFilter = (columns) => {
    const shown = new Set();
    const hidden = new Set();

    if (!Array.isArray(columns)) {
        return { shown, hidden };
    }

    columns
        .filter(entry => typeof entry === 'string')
        .map(entry => entry.trim())
        .filter(entry => entry.length > 0)
        .forEach(entry => {
            const sign = entry[0] === '-' || entry[0] === '+' ? entry[0] : '';
            const key = (sign ? entry.slice(1) : entry).trim();

            if (!key) {
                return;
            }

            const [add, remove] = sign === '-' ? [hidden, shown] : [shown, hidden];
            add.add(key);
            remove.delete(key);
        });

    return { shown, hidden };
};

// Compiles the config-authored `sortFns` (each `{ arguments, body }`) into callables.
const compileSortFns = (column) =>
    (column?.sortFns ?? []).map(fn =>
        typeof fn === 'function' ? fn : new Function(fn.arguments, fn.body)
    );

/**
 * Flattens the three column groups into one list of
 * `{ key, header, render(row), sortFns }`, reconciling the differing header
 * fields (`label` on base columns, `header` on config-authored ones).
 *
 * Base columns compute their value from the record, while config-authored ones
 * read a field that only exists once the row has been expanded.
 */
const normalizeColumns = (baseColumns = [], customColumns = [], customQuestions = []) => [
    ...baseColumns.map(column => ({
        key: column.key,
        header: column.label,
        render: column.valueFn,
        sortFns: column.sortFns ?? []
    })),
    ...[...customColumns, ...customQuestions].map(column => ({
        key: column.key,
        header: column.header,
        render: row => row[column.key],
        sortFns: compileSortFns(column)
    }))
];

// A group value carries no heading text when it is absent or only whitespace.
// `0` and `false` are values, not blanks.
const isBlankGroupValue = (value) =>
    value === null
    || value === undefined
    || (typeof value === 'string' && value.trim() === '');

/**
 * Splits records into ordered groups by one column's value.
 *
 * Records are first ordered by the column's own `sortFns`, then bucketed in
 * first-seen order, so the groups inherit that ordering. A `Map` is used rather
 * than a plain object because objects hoist integer-like keys into ascending
 * numeric order, which would discard the requested ordering. Records whose
 * value is blank collect into a single trailing group.
 *
 * Values are read through `render`, so the rows passed in must already carry
 * their expanded custom-column and custom-question fields. `key` is the
 * stringified value for identity, while `label` keeps the original so numbers
 * render as numbers.
 */
const groupRecords = (records = [], groupColumn) => {
    if (!groupColumn) {
        return [{ key: '', label: '', records }];
    }

    const sorted = orderBy(records, groupColumn.sortFns ?? []);
    const groups = new Map();
    const blanks = [];

    sorted.forEach(record => {
        const value = groupColumn.render(record);

        if (isBlankGroupValue(value)) {
            blanks.push(record);
            return;
        }

        const key = String(value);

        if (!groups.has(key)) {
            groups.set(key, { key, label: value, records: [] });
        }

        groups.get(key).records.push(record);
    });

    return [
        ...groups.values(),
        ...(blanks.length > 0 ? [{ key: '', label: '', records: blanks }] : [])
    ];
};

export {
    parseColumnFilter,
    compileSortFns,
    normalizeColumns,
    isBlankGroupValue,
    groupRecords
};
